/**
 * Where a path lives under the locale prefix this page was reached through.
 *
 * Navigation on a prefixed page links onward through the same prefix, so a
 * visit that starts on /en stays on addresses that declare English. Without it
 * a visitor sent to /en leaves the English addresses on their first click, and
 * every page they land on afterwards declares itself the Traditional Chinese
 * one while rendering in English.
 *
 * ⛔ The prefix, not the rendering language. A page with no prefix can render
 * in another language — the resolved locale is carried forward through a visit
 * — and keying on the language would walk a visitor from the unprefixed
 * addresses into the prefixed ones one click at a time. Moving somebody
 * between languages is the redirect's job, and it happens once.
 *
 * ⛔ Read from what the server stated on <body>, not from
 * window.location.pathname: an address rewritten by script no longer matches
 * what the page was served as.
 */

/**
 * The prefix this page was served under, or an empty string.
 * @returns {string}
 */
function servedPrefix() {
  try {
    return document.body?.dataset?.localePrefix || '';
  } catch (error) {
    // ⛔ Losing a prefix costs a visitor one language switch. Throwing here
    // costs them the navigation.
    console.warn('Failed to read the served locale prefix:', error);
    return '';
  }
}

/**
 * @param {string} path A site path, e.g. '/about' or '/faq#revenue-calculator'
 * @returns {string} The same path under the current prefix, or unchanged
 */
export function localePath(path) {
  if (typeof path !== 'string' || path === '') return path;

  // Anything that is not a site-relative path: mailto:, https://, a bare
  // fragment, and protocol-relative addresses, which start with two slashes
  // and point off-site.
  if (!path.startsWith('/') || path.startsWith('//')) return path;

  const prefix = servedPrefix();
  if (!prefix) return path;

  // ⛔ Only the current prefix is checked, and that is the whole contract:
  // callers pass an UNPREFIXED path. A path carrying some other locale's
  // prefix would be prefixed again — localePath('/zh-tw/faq') on /en returns
  // '/en/zh-tw/faq', which is a 404. Cross-locale links are not built here;
  // the language switcher (utils/languageSwitcher.js) posts to /locale and the
  // server decides the destination.
  //
  // ⛔ Detecting every served locale instead would put that list in the front
  // end, where it goes stale — and a stale list 404s exactly the pages it was
  // added to protect.
  if (path === prefix || path.startsWith(`${prefix}/`)) return path;

  // The prefixed form of '/' is the prefix itself: '/en', not '/en/'.
  return path === '/' ? prefix : `${prefix}${path}`;
}

export default localePath;
